import React, { useEffect, useState, useMemo } from "react";
import { useContentManagement } from "../../hooks/useContentManagement";
import TimezoneSelect from "../../components/TimezoneSelect";
import Select from "react-select";
import { toast } from "react-toastify"; 

export default function ContentManagementPage() {
    const { 
        settings, 
        isLoading, 
        updateSettings, 
        isUpdating,
        createSettings,
        isCreating
    } = useContentManagement();

    const [formData, setFormData] = useState({
        pushnotificaitontimezone: "",
        hour: "",
        minute: "",
    });

    const hourOptions = useMemo(() =>
        [...Array(24)].map((_, i) => {
            const value = i.toString().padStart(2, "0");
            return { value, label: value };
        }), []
    );

    const minuteOptions = useMemo(() =>
        [...Array(60)].map((_, i) => {
            const value = i.toString().padStart(2, "0");
            return { value, label: value };
        }), []
    );

    useEffect(() => {
        if (settings) {
            const [hour = "", minute = ""] = settings.pushnotificaitontime?.split(":") || [];
            setFormData({
                pushnotificaitontimezone: settings.pushnotificaitontimezone || "",
                hour,
                minute,
            });
        }
    }, [settings]);

    const handleSubmit = async () => {
        if (!formData.pushnotificaitontimezone || formData.hour === "" || formData.minute === "") {
            return toast.warning("Please select a timezone, hour, and minute.");
        }

        const payload = {
            pushnotificaitontimezone: formData.pushnotificaitontimezone,
            pushnotificaitontime: `${formData.hour}:${formData.minute}`,
        };

        try {
            // NEW LOGIC: Check if database already has settings
            if (settings?._id) {
                // If it exists, UPDATE it (PUT)
                await updateSettings({ id: settings._id, ...payload });
            } else {
                // If it does NOT exist (empty array in backend), CREATE it (POST)
                await createSettings(payload);
            }
        } catch (error) {
            console.error("Submission failed", error);
        }
    };

    // Calculate if either mutation is loading
    const isSaving = isUpdating || isCreating;

    if (isLoading) {
        return (
            <div className="p-4 text-center mt-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted fw-semibold">Loading Notification Settings...</p>
            </div>
        );
    }

    return (
        <div className="container py-4">
            <div className="card shadow-sm border-0">
                <div className="card-header bg-white border-bottom-0 pt-4 pb-0">
                    <h5 className="mb-0 text-primary fw-bold">Push Notification Settings</h5>
                    <p className="text-muted small mt-1">Configure when users will receive daily alerts.</p>
                </div>

                <div className="card-body mt-2">
                    <div className="row mb-4 align-items-center">
                        <div className="col-md-3 fw-semibold text-dark">Notification Timezone</div>
                        <div className="col-md-6">
                            <TimezoneSelect
                                value={formData.pushnotificaitontimezone}
                                onChange={(value) => setFormData((prev) => ({ ...prev, pushnotificaitontimezone: value }))}
                            />
                        </div>
                    </div>

                    <div className="row mb-4 align-items-center">
                        <div className="col-md-3 fw-semibold text-dark">Notification Time</div>
                        <div className="col-md-9 d-flex align-items-center gap-3">
                            <div style={{ width: 130 }}>
                                <label className="form-label small text-muted fw-semibold">Hour (24h)</label>
                                <Select
                                    options={hourOptions}
                                    value={hourOptions.find((o) => o.value === formData.hour) || null}
                                    onChange={(option) => setFormData((prev) => ({ ...prev, hour: option?.value || "" }))}
                                    placeholder="HH"
                                />
                            </div>

                            <div className="fs-4 mt-4 fw-bold text-muted">:</div>

                            <div style={{ width: 130 }}>
                                <label className="form-label small text-muted fw-semibold">Minute</label>
                                <Select
                                    options={minuteOptions}
                                    value={minuteOptions.find((o) => o.value === formData.minute) || null}
                                    onChange={(option) => setFormData((prev) => ({ ...prev, minute: option?.value || "" }))}
                                    placeholder="MM"
                                />
                            </div>
                        </div>
                    </div>

                    <hr className="my-4" />

                    <div className="text-md-end text-center mt-3 mb-2">
                        <button
                            className="btn btn-primary px-5 fw-semibold"
                            onClick={handleSubmit}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Saving...
                                </>
                            ) : (
                                "Save Settings"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}